/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, memo, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Component imports
import { Space, Typography } from 'antd';
import NodeWrapper from '../../NodeWrapper';
import LimitedConnectionsHandle from '../../LimitedConnectionsHandle';

// Misc imports
import { NODE_TYPES_PROPERTIES } from '../../../../constants/flowGraph';
import { FILE_UPLOAD_NODE_HANDLE_IDS, GRAPH_LAYOUTS } from '../../../../constants/flowGraph';
import { useBotLanguage } from '../../../../utils/hooks/useBotLanguage';
import { GraphContext } from '../../FlowGraph';
import { removeMarkdown } from '../../../../utils/string/removeMarkdown';

// Constant declarations
const { Text } = Typography;

const FileUploadNode = ({ id, type, data, dragging, selected }) => {
  const nodeProps = NODE_TYPES_PROPERTIES[type];
  const { t } = useTranslation();
  const { currentBotLanguage } = useBotLanguage();
  const { graphDirection } = useContext(GraphContext);

  const NodeContents = useMemo(() => {
    return (
      <Fragment>
        <LimitedConnectionsHandle type="target" position={graphDirection === GRAPH_LAYOUTS.VERTICAL ? 'top' : 'left'}/>

        <Space size={6} className="node-header">
          {nodeProps.icon}
          {nodeProps.label}
        </Space>

        {/* fixed width keep handles the same width regardless of the question length */}
        <Text type="secondary" className="px-2 text-truncate" style={{ width: '230px' }}>
          {data.label[currentBotLanguage] ? removeMarkdown(data.label[currentBotLanguage]) : t('Write some content')}
        </Text>

        <span className="cardWrapper">
          {t('Not accepted file format')}

          <LimitedConnectionsHandle
            id={`${id}-${FILE_UPLOAD_NODE_HANDLE_IDS.UNSUPPORTED_TYPE}`}
            type="source"
            position="right"
            allowedOutgoingEdgesCount={data?.supportedFileTypes?.length > 0 ? 1 : 0}
          />
        </span>

        <span className="cardWrapper">
          {t('Not accepted file size')}

          <LimitedConnectionsHandle
            id={`${id}-${FILE_UPLOAD_NODE_HANDLE_IDS.EXCEEDS_SIZE}`}
            type="source"
            position="right"
            allowedOutgoingEdgesCount={data?.maxFileSize ? 1 : 0}
          />
        </span>

        <span className="cardWrapper">
          {t('Typing instead of uploading')}

          <LimitedConnectionsHandle
            id={`${id}-${FILE_UPLOAD_NODE_HANDLE_IDS.NO_ATTACHMENT}`}
            type="source"
            position="right"
            allowedOutgoingEdgesCount={1}
          />
        </span>

        <LimitedConnectionsHandle
          id={id}
          type="source"
          position={graphDirection === GRAPH_LAYOUTS.VERTICAL ? 'bottom' : 'right'}
          allowedOutgoingEdgesCount={1}
        />
      </Fragment>
    );
  }, [data, nodeProps, graphDirection, currentBotLanguage]);

  return (
    <NodeWrapper
      id={id}
      type={type}
      data={data}
      children={NodeContents}
      dragging={dragging}
      selected={selected}
    />
  );
};

// https://reactflow.dev/docs/api/nodes/node-options/
FileUploadNode.propTypes = {
  data: PropTypes.object,
  id: PropTypes.string,
  dragging: PropTypes.bool,
  selected: PropTypes.bool,
  type: PropTypes.string,
  xPos: PropTypes.number,
  yPos: PropTypes.number
};

export default memo(FileUploadNode);
